import React, { Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { CssBaseline, ThemeProvider, Box, Skeleton } from '@mui/material';
import {
  Route,
  createRoutesFromElements,
  createBrowserRouter,
  RouterProvider,
  Outlet,
  useLocation,
} from 'react-router-dom';
import theme from './theme';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import ConsentBanner from './components/ConsentBanner';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy import with self-heal for stale deploys. When a code-split chunk 404s
// — a tab opened before a redeploy requests a chunk hash Vercel no longer
// serves — React throws ChunkLoadError. Rather than dump the user on the
// router error screen, force ONE full reload to fetch the current index.html +
// chunks. The timestamp guard prevents an infinite reload loop if the chunk is
// genuinely missing (a real build error rather than a stale shell).
const lazyWithReload = (factory) =>
  React.lazy(() =>
    factory().catch((err) => {
      const KEY = 'yad_chunk_reload_at';
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        return new Promise(() => {}); // hold render until the reload takes over
      }
      throw err; // already reloaded recently — surface the real error
    })
  );

// Lazy-loaded pages for code splitting
const HomePage = lazyWithReload(() => import('./pages/HomePage'));
const Stadiums = lazyWithReload(() => import('./pages/Stadiums'));
const Search = lazyWithReload(() => import('./pages/Search'));
const CountryStadiums = lazyWithReload(() => import('./pages/CountryStadiums'));
const WorldCup2026 = lazyWithReload(() => import('./pages/WorldCup2026'));
const WorldCupFixtures = lazyWithReload(() => import('./pages/WorldCupFixtures'));
const WorldCupBracket = lazyWithReload(() => import('./pages/WorldCupBracket'));
const WorldCupStadium = lazyWithReload(() => import('./pages/WorldCupStadium'));
const StadiumDetail = lazyWithReload(() => import('./pages/StadiumDetail'));
const Advertise = lazyWithReload(() => import('./pages/Advertise'));
const SavedTrips = lazyWithReload(() => import('./pages/SavedTrips'));
const Article = lazyWithReload(() => import('./pages/Article'));
const Moment = lazyWithReload(() => import('./pages/Moment'));
const NotFound = lazyWithReload(() => import('./pages/NotFound'));
const Planner = lazyWithReload(() => import('./pages/Planner'));
const PlannerSummary = lazyWithReload(() => import('./pages/PlannerSummary'));
const TripSheetPage = lazyWithReload(() => import('./pages/TripSheetPage'));
const AffiliateDisclosure = lazyWithReload(() => import('./pages/AffiliateDisclosure'));
const Privacy = lazyWithReload(() => import('./pages/Privacy'));
const Cookies = lazyWithReload(() => import('./pages/Cookies'));

const PageLoader = () => (
  <Box sx={{ maxWidth: 'lg', mx: 'auto', px: 3, py: 8, minHeight: '60vh' }}>
    <Skeleton variant="text" width="40%" height={48} sx={{ mb: 2 }} />
    <Skeleton variant="text" width="65%" height={24} sx={{ mb: 4 }} />
    <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2, mb: 3 }} />
    <Box sx={{ display: 'flex', gap: 3 }}>
      <Skeleton variant="rectangular" width="48%" height={160} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rectangular" width="48%" height={160} sx={{ borderRadius: 2 }} />
    </Box>
  </Box>
);

const Layout = () => {
  const location = useLocation();
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default'
      }}>
        <ScrollToTop />
        <Navbar />
        <Box
          component="main"
          sx={{
            flex: 1,
            pt: { xs: '56px', md: '72px' },
          }}
        >
          <Suspense fallback={<PageLoader />}>
            <ErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </Suspense>
        </Box>
        <Footer />
        <ConsentBanner />
        {/* Travelpayouts Drive is deliberately NOT mounted — see
            components/TravelpayoutsDrive.js for the measurement behind that. */}
      </Box>
    </ThemeProvider>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<Layout />}>
      <Route index element={<HomePage />} />
      <Route path="stadiums" element={<Stadiums />} />
      <Route path="search" element={<Search />} />
      <Route path="stadiums/:countryName" element={<CountryStadiums />} />
      <Route path="stadiums/:countryName/:stadiumSlug" element={<StadiumDetail />} />
      <Route path="world-cup-2026" element={<WorldCup2026 />} />
      <Route path="world-cup-2026/fixtures" element={<WorldCupFixtures />} />
      <Route path="world-cup-2026/bracket" element={<WorldCupBracket />} />
      <Route path="world-cup-2026/:stadiumSlug" element={<WorldCupStadium />} />
      <Route path="advertise" element={<Advertise />} />
      <Route path="my-trips" element={<SavedTrips />} />
      <Route path="planner" element={<Planner />} />
      <Route path="planner/:tripId" element={<PlannerSummary />} />
      {/* C4 — the trip sheet. /trip is the organiser's own copy; the
          published, forwardable /trip/:id arrives with C2. */}
      <Route path="trip" element={<TripSheetPage />} />
      <Route path="trip/:tripId" element={<TripSheetPage />} />
      <Route path="articles/:slug" element={<Article />} />
      <Route path="moments/:slug" element={<Moment />} />
      <Route path="affiliate-disclosure" element={<AffiliateDisclosure />} />
      <Route path="privacy" element={<Privacy />} />
      <Route path="cookies" element={<Cookies />} />
      <Route path="*" element={<NotFound />} />
    </Route>
  ),
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  }
);

function App() {
  return (
    <HelmetProvider>
      <RouterProvider router={router} />
    </HelmetProvider>
  );
}

export default App;
